import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import ButtonNormalText from "../Buttons/ButtonNormalText";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import "./TableStyle.css";
import { dataService } from "../../../Services/data/data.service";
import { makeStyles } from "@material-ui/core/styles";
import ColorPicker from "../ColorPicker";
import { controlTypes, externalApiTypes, costBundleTypes, externalApiEnumBehaviorTypes, flowBoxButtonAction } from "../../../Config/constants";
import Input from "../Input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectGroup, SelectLabel } from "../Select";
import { Fab, Paper, IconButton, DialogTitle, ToggleButton, ToggleButtonGroup, DialogContentText, DialogContent, DialogActions, Dialog, MenuItem, ListSubheader, Checkbox } from "@mui/material";
import { Plus, Minus, Trash2, Pencil, Save, GripVertical, X, QrCode, Braces, Timer, Hash, List as ListIcon, Anchor, Pin, ChevronDown, Cpu } from "lucide-react";
import TypePickerDialog from "./TypePickerDialog";
import Tooltip from "../../Shared/Tooltip";
import Chip from "../ui/Chip";
import EditCostBundleDialog from "../../Pages/ModifyProject/Components/EditCostBundleDialog";
import QrCodeDialog from "../Dialogs/QrCodeDialog";
import ApiHelper from "../../../Utils/API/apiHelper";
import { useErrorHandler } from "../../../hooks/useErrorHandler";
import ErrorHandlerService from "../../../Services/ErrorHandlerService";

// Physical FlowBox button dome colors, keyed by button index (matches FlowBoxDeviceConfigureDialog)
const FLOWBOX_BUTTON_COLORS = {
  1: { highlight: "#6ab2ff", mid: "#1258c4", shadow: "#082458" }, // Blue
  2: { highlight: "#f5f268", mid: "#b0bc00", shadow: "#6c7200" }, // Yellow
  3: { highlight: "#f56a6a", mid: "#c81010", shadow: "#760808" }, // Red
  4: { highlight: "#4de86a", mid: "#0ca030", shadow: "#065518" }, // Green
  5: { highlight: "#ffffff", mid: "#dcdcdc", shadow: "#a8a8a8" }, // White
};

const TableDragResources = forwardRef((props, ref) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { error, isLoading, handleError, clearError, executeWithErrorHandling } = useErrorHandler();
  const [open, setOpen] = useState(false); // Row error dialog
  const [types, setTypes] = useState([]);
  
  // Edit Cost Bundle Dialog state
  const [editCostBundleDialogOpen, setEditCostBundleDialogOpen] = useState(false);
  const [pendingPriceBundleChange, setPendingPriceBundleChange] = useState(null);
  const [lists, setLists] = useState([]);
  const [apis, setApis] = useState([]);
  const [valueTypes, setValueTypes] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [originalPriceBundles, setOriginalPriceBundles] = useState([]);
  const [allPriceBundles, setAllPriceBundles] = useState([]);
  const [expandTable, setExpandTable] = useState(false);
  const [currency, setCurrency] = useState({ symbol: "€" });

  const [openAllRowsErrorsDialog, setOpenAllRowsErrorsDialog] = useState(false);

  // Type picker dialog state
  const [typePickerOpen, setTypePickerOpen] = useState(false);
  const [typePickerRowId, setTypePickerRowId] = useState(null);

  // Anchor API QR dialog state
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrDialogApiName, setQrDialogApiName] = useState("");
  const [qrDialogItems, setQrDialogItems] = useState([]);
  const [qrDialogLoading, setQrDialogLoading] = useState(false);

  const getTypeCompositeValue = (row) => {
    if (!row.type) return null;
    if (row.type === 4 && row.dataListId) return `list:${row.dataListId}`;
    if (row.type === 7 && row.externalApiId) return `api:${row.externalApiId}`;
    return `type:${row.type}`;
  };

  const handleTypeSelectChange = (compositeValue, rowId) => {
    if (!compositeValue) return;
    const [category, id] = compositeValue.split(':');
    const numId = parseInt(id);
    switch (category) {
      case 'type':
        setType(numId, rowId);
        break;
      case 'list': {
        const listRow = lists.find(l => l.id === numId);
        if (listRow) setTypeDataList(rowId, listRow);
        break;
      }
      case 'api': {
        const apiRow = apis.find(a => a.id === numId);
        if (apiRow) setTypeExternalApi(rowId, apiRow);
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load types if not already loaded
        if (types.length === 0 || types === undefined) {
          const typesResult = await dataService.getTypes();
          setTypes(typesResult);
        }

        // Load all required data concurrently
        const [valueTypesResult, accountsResult, priceBundlesResult, listsResult, apisResult] = await Promise.all([
          dataService.getValueTypes(props.Login.userid),
          dataService.getAllUserCompanyAccounts(props.Login.userid, 0, 0),
          dataService.getAllProjectAvailablePriceBundles(props.Login.userid),
          dataService.getLists(props.Login.userid),
          dataService.getApis(props.Login.userid)
        ]);

        setValueTypes(valueTypesResult.sort((a, b) => a.name.localeCompare(b.name)));
        setAllAccounts(accountsResult);
        setOriginalPriceBundles(priceBundlesResult);
        // Apply company filter if companyId is already available
        const filteredPriceBundles = props.companyId 
          ? priceBundlesResult.filter((priceBundle) => priceBundle.companyId === props.companyId)
          : priceBundlesResult;
        setAllPriceBundles(filteredPriceBundles);
        setLists(listsResult.sort((a, b) => a.name.localeCompare(b.name)));
        setApis(apisResult.sort((a, b) => a.name.localeCompare(b.name)));

      } catch (error) {
        handleError(error, t("tableDragResources.loadError"));
      }
    };

    loadInitialData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (props.currency) {
      setCurrency(props.currency);
    }
  }, [props.currency]);

  useEffect(() => {
    // when company is selected, make sure only price bundles from that company are shown
    if (originalPriceBundles.length > 0) {
      if (props.companyId) {
        const filteredPriceBundles = originalPriceBundles.filter(
          (priceBundle) => priceBundle.companyId === props.companyId
        );
        setAllPriceBundles(filteredPriceBundles);
      } else {
        setAllPriceBundles(originalPriceBundles);
      }
    }
  }, [props.companyId, originalPriceBundles]);

  // Auto-create the first row when starting with an empty table
  useEffect(() => {
    if (props.rows && props.rows.length === 0) {
      const firstRow = {
        Id: 1,
        description: "",
        type: null,
        accountId: null,
        priceBundleId: null,
        edit: true,
        name: "",
        sequence: 1,
        comment: false,
        isParent: false,
        newRow: true,
        price: 0,
        costProfit: "+",
        isProfit: true,
        descriptionError: null,
        accountError: null,
        typeError: null,
      };
      props.setRows([firstRow]);
    }
  }, [props.rows, props.setRows]);

  const handleDialogOpen = () => {
    setOpenAllRowsErrorsDialog(true);
  };

  const handleDialogClose = () => {
    setOpenAllRowsErrorsDialog(false);
  };

  const selectType = (row) => {
    switch (row.type) {
      case 1:
        return t("tableDragResources.typeCounter");
      case 2:
        return t("tableDragResources.typeStopwatch");
      case 3:
        return t("tableDragResources.typeFreeText");
      case 4:
        if (lists !== undefined && lists.length > 0) {
          return lists.find((x) => x.id === row.dataListId).name;
        }
        break;
      case 5:
        return t("tableDragResources.typeScanner");
      case 6:
        return t("tableDragResources.typePicture");
      case 7:
        if (apis !== undefined && apis.length > 0 && row.externalApiId !== null) {
          return apis.find((x) => x.id === row.externalApiId).name;
        }
        break;
      default:
        break;
      // eslint-disable-next-line
    }
  };

  // function to determine if the pricing should be per event or per hour
  const getPricingType = (row) => {
    // If the type is stopwatch, or the type is api and the externalApiTypeId is stopwatch then return price/hour
    switch (row.type) {
      case 2:
        return "price/hour";
      case 7:
        if (apis !== undefined && apis.length > 0 && row.externalApiId !== null) {
          if (apis.find((x) => x.id === row.externalApiId).externalApiTypeId === externalApiTypes.STOPWATCH) {
            return "price/hour";
          } else {
            return "price/event";
          }
        }
        break;
      default:
        return "price/event";
    }
  };

  // function to filter price bundles based on the row type (time-based vs event-based)
  const getFilteredPriceBundles = (row) => {
    if (!allPriceBundles || allPriceBundles.length === 0) return [];

    // If no type is selected, show all price bundles
    if (!row.type) {
      return allPriceBundles;
    }

    // Determine pricing type directly and filter accordingly
    switch (row.type) {
      case 2: // Stopwatch - uses TIME bundles
        return allPriceBundles.filter((priceBundle) => priceBundle.costBundleTypeId === costBundleTypes.TIME);
      case 7: // External API - depends on API type
        if (apis !== undefined && apis.length > 0 && row.externalApiId !== null) {
          const api = apis.find((x) => x.id === row.externalApiId);
          if (api && api.externalApiTypeId === externalApiTypes.STOPWATCH) {
            return allPriceBundles.filter((priceBundle) => priceBundle.costBundleTypeId === costBundleTypes.TIME);
          }
        }
        return allPriceBundles.filter((priceBundle) => priceBundle.costBundleTypeId === costBundleTypes.EVENT);
      default: // All other types (Counter, Text, DataList, Scanner, Picture) - use EVENT bundles
        return allPriceBundles.filter((priceBundle) => priceBundle.costBundleTypeId === costBundleTypes.EVENT);
    }
  };

  const selectTypeId = (row) => {
    switch (row.type) {
      case 1:
        return 1;
      case 2:
        return 2;
      case 3:
        return 3;
      case 4:
        if (lists !== undefined && lists.length > 0) {
          return lists.find((x) => x.id === row.dataListId).name;
        }
        break;
      case 5:
        return 5;
      case 6:
        return 6;
      case 7:
        if (apis !== undefined && apis.length > 0 && row.externalApiId !== null) {
          return apis.find((x) => x.id === row.externalApiId).name;
        }
        break;
      default:
        break;
      // eslint-disable-next-line
    }
  };
  const getRanHex = (size) => {
    let result = [];
    let hexRef = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "a", "b", "c", "d", "e", "f"];

    for (let n = 0; n < size; n++) {
      result.push(hexRef[Math.floor(Math.random() * 16)]);
    }
    return result.join("");
  };

  const setType = (typeId, id) => {
    if (typeId < 9) {
      let tempArray = props.rows.map((x) => ({ ...x }));
      if (typeId !== (controlTypes.CAMERA || controlTypes.COUNTER || controlTypes.SCANNER)) {
        tempArray.find((x) => x.Id === id).comment = false;
      }
      if (typeId !== (controlTypes.FREETEXT || controlTypes.DATALIST || controlTypes.SCANNER)) {
        tempArray.find((x) => x.Id === id).isParent = false;
      }
      if (tempArray.find((x) => x.Id === id) !== undefined) {
        const row = tempArray.find((x) => x.Id === id);
        row.type = typeId;
        row.typeId = typeId;

        // Check if current price bundle is compatible with new type
        if (row.priceBundleId) {
          const currentPriceBundle = allPriceBundles.find((pb) => pb.id === row.priceBundleId);
          if (currentPriceBundle) {
            const newPricingType = getPricingType({ ...row, type: typeId });
            const isCompatible =
              (newPricingType === "price/hour" && currentPriceBundle.costBundleTypeId === costBundleTypes.TIME) ||
              (newPricingType === "price/event" && currentPriceBundle.costBundleTypeId === costBundleTypes.EVENT);

            if (!isCompatible) {
              row.priceBundleId = null; // Reset incompatible price bundle
            }
          }
        }

        if (row.color === undefined) {
          switch (typeId) {
            case 1:
              row.color = "#" + getRanHex(6);
              row.name = t("tableDragResources.typeCounter");
              break;
            case 2:
              row.color = "#" + getRanHex(6);
              row.name = t("tableDragResources.typeStopwatch");
              break;
            case 3:
              row.color = "#" + getRanHex(6);
              row.name = t("tableDragResources.typeFreeText");
              break;
            case 5:
              row.color = "#" + getRanHex(6);
              row.name = t("tableDragResources.typeScanner");
              break;
            case 6:
              row.color = "#" + getRanHex(6);
              row.name = t("tableDragResources.typePicture");
              break;
            default:
              break;
            // eslint-disable-next-line
          }
        }
      } else {
        const editRow = tempArray.find((x) => x.edit);
        editRow.type = typeId;
        editRow.typeId = typeId;

        // Check if current price bundle is compatible with new type
        if (editRow.priceBundleId) {
          const currentPriceBundle = allPriceBundles.find((pb) => pb.id === editRow.priceBundleId);
          if (currentPriceBundle) {
            const newPricingType = getPricingType({ ...editRow, type: typeId });
            const isCompatible =
              (newPricingType === "price/hour" && currentPriceBundle.costBundleTypeId === costBundleTypes.TIME) ||
              (newPricingType === "price/event" && currentPriceBundle.costBundleTypeId === costBundleTypes.EVENT);

            if (!isCompatible) {
              editRow.priceBundleId = null; // Reset incompatible price bundle
            }
          }
        }

        if (tempArray.find((x) => x.Id === id).color === undefined) {
          switch (typeId) {
            case 1:
              tempArray.find((x) => x.Id === id).color = "#" + getRanHex(6);
              tempArray.find((x) => x.Id === id).name = t("tableDragResources.typeCounter");
              break;
            case 2:
              tempArray.find((x) => x.Id === id).color = "#" + getRanHex(6);
              tempArray.find((x) => x.Id === id).name = t("tableDragResources.typeStopwatch");
              break;
            case 3:
              tempArray.find((x) => x.Id === id).color = "#" + getRanHex(6);
              tempArray.find((x) => x.Id === id).name = t("tableDragResources.typeFreeText");
              break;
            case 5:
              tempArray.find((x) => x.Id === id).color = "#" + getRanHex(6);
              tempArray.find((x) => x.Id === id).name = t("tableDragResources.typeScanner");
              break;
            case 6:
              tempArray.find((x) => x.Id === id).color = "#" + getRanHex(6);
              tempArray.find((x) => x.Id === id).name = t("tableDragResources.typePicture");
              break;
            default:
              break;
            // eslint-disable-next-line
          }
        }
      }
      props.setRows(tempArray);
    }
  };

  const setTypeDataList = (id, DataListrow) => {
    let tempArray = props.rows.map((x) => ({ ...x }));
    if (tempArray.find((x) => x.Id === id) !== undefined) {
      const row = tempArray.find((x) => x.Id === id);
      row.type = 4;
      row.typeId = 4;
      row.color = "#" + getRanHex(6);
      row.dataListId = DataListrow.id;
      row.name = `${t("tableDragResources.listPrefix")}${DataListrow.name}`;

      if (row.priceBundleId) {
        const currentPriceBundle = allPriceBundles.find((pb) => pb.id === row.priceBundleId);
        if (currentPriceBundle && currentPriceBundle.costBundleTypeId !== costBundleTypes.EVENT) {
          row.priceBundleId = null; // Reset incompatible price bundle
        }
      }
    } else {
      const editRow = tempArray.find((x) => x.edit);
      editRow.type = 4;
      editRow.typeId = 4;
      editRow.color = "#" + getRanHex(6);
      editRow.dataListId = DataListrow.id;
      editRow.name = `${t("tableDragResources.listPrefix")}${DataListrow.name}`;

      // Check if current price bundle is compatible with DataList type (event-based)
      if (editRow.priceBundleId) {
        const currentPriceBundle = allPriceBundles.find((pb) => pb.id === editRow.priceBundleId);
        if (currentPriceBundle && currentPriceBundle.costBundleTypeId !== costBundleTypes.EVENT) {
          editRow.priceBundleId = null; // Reset incompatible price bundle
        }
      }
    }
    props.setRows(tempArray);
  };

  const hasAnchorApiInProject = (excludeRowId) => {
    return props.rows.some((row) => {
      if (row.Id === excludeRowId) return false;
      if (!row.externalApiId || row.type !== 7) return false;
      const api = apis.find((a) => a.id === row.externalApiId);
      return api && api.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.ANCHOR;
    });
  };

  const getApiTypeIcon = (apiItem) => {
    if (apiItem.externalApiTypeId === externalApiTypes.ENUM) {
      if (apiItem.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.ANCHOR) {
        return <Anchor className={classes.apiItemIcon} />;
      }
      if (apiItem.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.STICKY) {
        return <Pin className={classes.apiItemIcon} />;
      }
      return <ListIcon className={classes.apiItemIcon} />;
    }
    switch (apiItem.externalApiTypeId) {
      case externalApiTypes.DATA:
        return <Braces className={classes.apiItemIcon} />;
      case externalApiTypes.STOPWATCH:
        return <Timer className={classes.apiItemIcon} />;
      case externalApiTypes.NUMERIC:
        return <Hash className={classes.apiItemIcon} />;
      default:
        return null;
    }
  };

  const isAnchorOrStickyApi = (row) => {
    if (row.type !== 7 || !row.externalApiId) return false;
    const api = apis.find((a) => a.id === row.externalApiId);
    return api && (api.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.ANCHOR || api.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.STICKY);
  };

  const isAnchorApi = (row) => {
    if (row.type !== 7 || !row.externalApiId) return false;
    const api = apis.find((a) => a.id === row.externalApiId);
    return api && api.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.ANCHOR;
  };

  // FlowBox usages for a given external-API-typed row (buttons that drive this API)
  const getFlowBoxUsages = (row) => {
    if (row.type !== 7 || !row.externalApiId) return [];
    const map = props.flowBoxUsagesByApiId;
    if (!map) return [];
    return map[row.externalApiId] || [];
  };

  const flowBoxActionLabel = (apiActionId) => {
    switch (apiActionId) {
      case flowBoxButtonAction.START:
        return t("flowBox.action.start");
      case flowBoxButtonAction.STOP:
        return t("flowBox.action.stop");
      case flowBoxButtonAction.TOGGLE:
        return t("flowBox.action.toggle");
      case flowBoxButtonAction.SUBTRACT:
        return t("flowBox.action.subtract");
      case flowBoxButtonAction.LOG_DURATION:
        return t("flowBox.action.logDuration");
      case flowBoxButtonAction.MEASURE:
      default:
        return t("flowBox.action.measure");
    }
  };

  const formatFlowBoxDuration = (totalSeconds) => {
    if (!totalSeconds || totalSeconds <= 0) return "-";
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return [h ? `${h}h` : null, m ? `${m}m` : null, s ? `${s}s` : null].filter(Boolean).join(" ");
  };

  // Human-readable detail for a FlowBox button: duration for stopwatch, otherwise count per press
  const flowBoxUsageDetail = (usage) => {
    if (usage.externalApiTypeId === externalApiTypes.STOPWATCH) {
      if (usage.apiActionId === flowBoxButtonAction.LOG_DURATION) {
        return `${t("flowBox.action.logDuration")} - ${formatFlowBoxDuration(usage.fixedDurationSeconds)}`;
      }
      return flowBoxActionLabel(usage.apiActionId);
    }
    const sign = usage.apiActionId === flowBoxButtonAction.SUBTRACT ? "-" : "+";
    return t("tableDragResources.flowBoxPerPress", { count: `${sign}${usage.countPerPress ?? 1}` });
  };

  const renderFlowBoxButtonDome = (buttonIndex) => {
    const colors = FLOWBOX_BUTTON_COLORS[buttonIndex] || FLOWBOX_BUTTON_COLORS[1];
    return (
      <span
        className={classes.flowBoxTooltip__dome}
        style={{ background: `radial-gradient(circle at 36% 30%, ${colors.highlight} 0%, ${colors.mid} 48%, ${colors.shadow} 100%)` }}
      />
    );
  };

  const renderFlowBoxTooltip = (usages) => {
    // Group button usages per device, keeping first-seen order
    const devices = [];
    usages.forEach((usage) => {
      let device = devices.find((d) => d.deviceId === usage.deviceId);
      if (!device) {
        device = { deviceId: usage.deviceId, displayName: usage.displayName, imei: usage.imei, buttons: [] };
        devices.push(device);
      }
      device.buttons.push(usage);
    });

    return (
      <div className={classes.flowBoxTooltip}>
        <div className={classes.flowBoxTooltip__title}>
          <Cpu size={13} />
          {t("tableDragResources.flowBoxUsageTitle")}
        </div>
        {devices.map((device, index) => (
          <div
            key={device.deviceId}
            className={classes.flowBoxTooltip__device}
            style={index > 0 ? { marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #EDEDED" } : undefined}
          >
            <div className={classes.flowBoxTooltip__name}>{device.displayName || t("tableDragResources.flowBoxUnnamed")}</div>
            <div className={classes.flowBoxTooltip__imei}>{device.imei || t("tableDragResources.flowBoxNoImei")}</div>
            <div className={classes.flowBoxTooltip__buttons}>
              {device.buttons
                .slice()
                .sort((a, b) => b.buttonIndex - a.buttonIndex)
                .map((button) => (
                  <div key={button.buttonIndex} className={classes.flowBoxTooltip__buttonRow}>
                    {renderFlowBoxButtonDome(button.buttonIndex)}
                    <span className={classes.flowBoxTooltip__detail}>{flowBoxUsageDetail(button)}</span>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleOpenQrDialog = (row) => {
    const api = apis.find((a) => a.id === row.externalApiId);
    if (!api) return;
    const appSettings = ApiHelper.appSettings;
    setQrDialogApiName(row.description || api.name);
    setQrDialogItems([]);
    setQrDialogLoading(true);
    setQrDialogOpen(true);
    dataService.getEnumOptionsByExternalApiId(api.id).then((results) => {
      setQrDialogItems(
        (results || []).map((option) => ({
          key: option.id,
          label: option.value,
          value: `${appSettings.TAPLR_API_BASE_URL}/ExternalApiMeasurementLogic/NewExternalApiEnumBehaviorMeasurementQuery?apiKey=${api.apiKey}&enumOptionId=${option.id}`,
          description: option.description,
        }))
      );
      setQrDialogLoading(false);
    });
  };

  const setTypeExternalApi = (id, ExternalApirow) => {
    // Prevent adding more than one anchor API to a project
    if (ExternalApirow.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.ANCHOR) {
      const editRowId = id ?? props.rows.find((x) => x.edit)?.Id;
      if (hasAnchorApiInProject(editRowId)) {
        return;
      }
    }

    let tempArray = props.rows.map((x) => ({ ...x }));
    if (tempArray.find((x) => x.Id === id) !== undefined) {
      const row = tempArray.find((x) => x.Id === id);
      row.type = 7;
      row.typeId = 7;
      row.color = "#" + getRanHex(6);
      row.externalApiId = ExternalApirow.id;
      row.name = `${t("tableDragResources.apiPrefix")}${ExternalApirow.name}`;

      // Anchor/Sticky APIs cannot have a price bundle or value type
      const isBehaviorApi = ExternalApirow.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.ANCHOR || ExternalApirow.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.STICKY;
      if (isBehaviorApi) {
        row.priceBundleId = null;
        row.valueTypeId = null;
      } else if (row.priceBundleId) {
        // Check if current price bundle is compatible with External API type
        const currentPriceBundle = allPriceBundles.find((pb) => pb.id === row.priceBundleId);
        if (currentPriceBundle) {
          const newPricingType = getPricingType({ ...row, type: 7, externalApiId: ExternalApirow.id });
          const isCompatible =
            (newPricingType === "price/hour" && currentPriceBundle.costBundleTypeId === costBundleTypes.TIME) ||
            (newPricingType === "price/event" && currentPriceBundle.costBundleTypeId === costBundleTypes.EVENT);

          if (!isCompatible) {
            row.priceBundleId = null; // Reset incompatible price bundle
          }
        }
      }
    } else {
      const editRow = tempArray.find((x) => x.edit);
      editRow.type = 7;
      editRow.typeId = 7;
      editRow.color = "#" + getRanHex(6);
      editRow.externalApiId = ExternalApirow.id;
      editRow.name = `${t("tableDragResources.apiPrefix")}${ExternalApirow.name}`;

      // Anchor/Sticky APIs cannot have a price bundle or value type
      const isBehaviorApiEdit = ExternalApirow.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.ANCHOR || ExternalApirow.externalApiEnumBehaviorTypeId === externalApiEnumBehaviorTypes.STICKY;
      if (isBehaviorApiEdit) {
        editRow.priceBundleId = null;
        editRow.valueTypeId = null;
      } else if (editRow.priceBundleId) {
        // Check if current price bundle is compatible with External API type
        const currentPriceBundle = allPriceBundles.find((pb) => pb.id === editRow.priceBundleId);
        if (currentPriceBundle) {
          const newPricingType = getPricingType({ ...editRow, type: 7, externalApiId: ExternalApirow.id });
          const isCompatible =
            (newPricingType === "price/hour" && currentPriceBundle.costBundleTypeId === costBundleTypes.TIME) ||
            (newPricingType === "price/event" && currentPriceBundle.costBundleTypeId === costBundleTypes.EVENT);

          if (!isCompatible) {
            editRow.priceBundleId = null; // Reset incompatible price bundle
          }
        }
      }
    }
    props.setRows(tempArray);
  };

  const setValueType = (id, valueType) => {
    let tempArray = props.rows.map((x) => ({ ...x }));
    if (tempArray.find((x) => x.Id === id) !== undefined) {
      tempArray.find((x) => x.Id === id).valueTypeId = valueType;
    }
    props.setRows(tempArray);
  };

  const setAccount = (id, accountId) => {
    let tempArray = props.rows.map((x) => ({ ...x }));
    if (tempArray.find((x) => x.Id === id) !== undefined) {
      tempArray.find((x) => x.Id === id).accountId = accountId;
    }
    props.setRows(tempArray);
  };

  const setPriceBundle = async (id, priceBundleId) => {
    const row = props.rows.find((x) => x.Id === id);
    if (!row) return;

    // Check if this row has measurements and the price bundle is actually changing
    if (row.hasMeasurements && row.priceBundleId !== priceBundleId) {
      // Store the pending change and open the dialog
      setPendingPriceBundleChange({
        rowId: id,
        newPriceBundleId: priceBundleId,
        oldPriceBundleId: row.priceBundleId,
        rowDescription: row.description
      });
      setEditCostBundleDialogOpen(true);
      return;
    }

    // No measurements or no change, proceed directly
    let tempArray = props.rows.map((x) => ({ ...x }));
    if (tempArray.find((x) => x.Id === id) !== undefined) {
      tempArray.find((x) => x.Id === id).priceBundleId = priceBundleId;
    }
    props.setRows(tempArray);
  };

  const setDescription = (event, id) => {
    let tempArray = props.rows.map((x) => ({ ...x }));
    if (tempArray.find((x) => x.Id === id) !== undefined) {
      tempArray.find((x) => x.Id === id).description = event.target.value;
    } else {
      tempArray.find((x) => x.edit).description = event.target.value;
    }
    props.setRows(tempArray);
  };

  const setSessionDurationMinutes = (event, id) => {
    let tempArray = props.rows.map((x) => ({ ...x }));
    const inputValue = event.target.value;
    const value = inputValue === "" ? null : parseInt(inputValue, 10);

    if (tempArray.find((x) => x.Id === id) !== undefined) {
      tempArray.find((x) => x.Id === id).sessionDurationMinutes = value;
    } else {
      tempArray.find((x) => x.edit).sessionDurationMinutes = value;
    }

    props.setRows(tempArray);
  };

  const setPrice = (event, id) => {
    let tempArray = props.rows.map((x) => ({ ...x }));
    const inputValue = event.target.value; // Get the input value as a string

    if (tempArray.find((x) => x.Id === id) !== undefined) {
      tempArray.find((x) => x.Id === id).price = inputValue; // Keep it as a string
    } else {
      tempArray.find((x) => x.edit).price = inputValue; // Keep it as a string
    }

    props.setRows(tempArray);
  };

  const handleCostProfit = (event, id) => {
    let tempArray = props.rows.map((x) => ({ ...x }));
    if (tempArray.find((x) => x.Id === id) !== undefined) {
      tempArray.find((x) => x.Id === id).costProfit = event;
      if (event === "+") {
        tempArray.find((x) => x.Id === id).isProfit = true;
      } else {
        tempArray.find((x) => x.Id === id).isProfit = false;
      }
    } else {
      tempArray.find((x) => x.edit).costProfit = event;
      if (event === "+") {
        tempArray.find((x) => x.Id === id).isProfit = true;
      } else {
        tempArray.find((x) => x.Id === id).isProfit = false;
      }
    }
    props.setRows(tempArray);
  };

  const GenerateId = () => {
    let tempArray = props.rows.map((x) => ({ ...x }));
    const ids = tempArray.map((object) => {
      return object.Id;
    });
    const max = Math.max(...ids);
    if (tempArray.length === 0) {
      return 1;
    } else {
      let index = max;
      index++;
      return index;
    }
  };

  const editProjectRow = (id) => {
    let tempArray = props.rows.map((x) => ({ ...x }));
    tempArray.find((x) => x.Id === id).edit = true;
    props.setRows(tempArray);
  };

  const saveProjectRow = (id) => {
    let tempArray = props.rows.map((x) => ({ ...x }));

    if (validateRow(id)) {
      tempArray.find((x) => x.Id === id).edit = false;
      props.setRows(tempArray);
      props.makeSave(tempArray);
    } else {
      handleDialogOpen();
    }
  };

  const deleteProjectRow = (id) => {
    let tempArray = props.rows.map((x) => ({ ...x }));
    let index = tempArray.indexOf(tempArray.find((x) => x.Id === id));
    tempArray.splice(index, 1);
    props.setRows(tempArray);
    props.makeSave(tempArray);
  };

  const addRow = () => {
    var tempArray = props.rows.map((x) => ({ ...x }));
    if (tempArray.find((x) => x.type === null || x.description === "") === undefined) {
      let Rowid = {
        Id: GenerateId(),
        description: "",
        type: null,
        accountId: null,
        priceBundleId: null,
        edit: true,
        name: "",
        sequence: GenerateId(),
        comment: false,
        isParent: false,
        newRow: true,
        price: 0,
        costProfit: "+",
        isProfit: true,
        descriptionError: null,
        accountError: null,
        typeError: null,
      };
      let PrevId = GenerateId() - 1;
      if (tempArray.length !== 0) {
        if (tempArray.find((x) => x.Id === PrevId).description !== "" && tempArray.find((x) => x.Id === PrevId).type !== null) {
          tempArray.find((x) => x.Id === PrevId).edit = false;
          tempArray.push(Rowid);
          props.setRows(tempArray);
        } else {
          tempArray.push(Rowid);
          props.setRows(tempArray);
        }
      } else {
        tempArray.push(Rowid);
        props.setRows(tempArray);
      }
    }
  };

  const handleColor = (event, id) => {
    let tempArray = props.rows.map((x) => ({ ...x }));
    if (tempArray.find((x) => x.Id === id) !== undefined) {
      tempArray.find((x) => x.Id === id).color = event;
    } else {
      tempArray.find((x) => x.edit).color = event;
    }
    props.setRows(tempArray);
  };

  const handleComment = (id) => {
    let tempArray = props.rows.map((x) => ({ ...x }));
    if (tempArray.find((x) => x.Id === id) !== undefined) {
      tempArray.find((x) => x.Id === id).comment = !tempArray.find((x) => x.Id === id).comment;
    } else {
      tempArray.find((x) => x.edit).comment = !tempArray.find((x) => x.Id === id).comment;
    }
    props.setRows(tempArray);
  };

  const handleParent = (id) => {
    let tempArray = props.rows.map((row) => ({
      ...row,
      comment: row.Id === id ? false : row.comment, // Set all others to false
      isParent: row.Id === id ? !row.isParent : false, // Set all others to false
    }));
    props.setRows(tempArray);
  };

  const MoveSequence = (source, destination) => {
    let tempArray = props.rows.map((x) => ({ ...x }));
    tempArray.forEach(function (value, i) {
      value.sequence = i;
    });
    tempArray[source].sequence = -1;
    if (source > destination) {
      tempArray
        .filter((x) => x.sequence >= destination && x.sequence !== -1 && x.sequence < source)
        .forEach((x) => {
          x.sequence++;
        });
    } else if (source < destination) {
      tempArray
        .filter((x) => x.sequence <= destination && x.sequence !== -1 && x.sequence > source)
        .forEach((x) => {
          x.sequence--;
        });
    }

    tempArray.find((x) => x.sequence === -1).sequence = destination;
    tempArray.sort((a, b) => a.sequence - b.sequence);
    props.setRows(tempArray);
    Shrink();
  };

  const Expand = () => {
    setExpandTable(true);
  };

  const Shrink = () => {
    setExpandTable(false);
  };

  function handleOnDragEnd(result) {
    if (result.destination) {
      MoveSequence(result.source.index, result.destination.index);
    }
    if (!result.destination) return;
  }

  const formatAccountCategory = (id) => {
    if (allAccounts && allAccounts.length > 0) {
      let accountCategory = allAccounts.find((company) => company.id === props.companyId).accountCategories.find((accountCategory) => accountCategory.id === id);
      return accountCategory.name;
    }
  };

  const formatAccount = (id) => {
    if (id !== null) {
      if (allAccounts && allAccounts.length > 0) {
        let account;
        let companyAccounts = allAccounts.find((company) => company.id === props.companyId);

        // find account in all companyAccounts.accountCategories
        companyAccounts.accountCategories.forEach((category) => {
          let tempAccount = category.accounts.find((account) => account.id === id);
          if (tempAccount !== undefined && tempAccount !== null) {
            account = tempAccount;
          }
        });

        return account.name;
      }
    } else {
      return "";
    }
  };

  const formatValueType = (id) => {
    if (valueTypes && valueTypes.length > 0) {
      let valueType = valueTypes.find((valueType) => valueType.id === id);
      if (!valueType) return "";
      const key = `tableDragResources.valueTypes.${valueType.id}`;
      const translated = t(key, { defaultValue: valueType.name });
      return translated === key ? valueType.name : translated;
    }
  };

  const getValueTypeLabel = (valueType) => {
    if (!valueType) return "";
    const key = `tableDragResources.valueTypes.${valueType.id}`;
    const translated = t(key, { defaultValue: valueType.name });
    return translated === key ? valueType.name : translated;
  };

  const getTotalBundlePrice = (priceBundleItems) => {
    // sum of all non fixed priceBundleItem.price * priceBundleItem.multiplier
    if (priceBundleItems && priceBundleItems.length > 0) {
      return (
        currency.symbol +
        priceBundleItems
          .reduce((total, item) => {
            if (item.isFixed) return total;
            const price = typeof item.price === "string" ? parseFloat(item.price.replace(",", ".")) : parseFloat(item.price || 0);
            return total + price * (item.multiplier || 1);
          }, 0)
          .toFixed(3)
      );
    }
    return "0.00 " + currency.symbol;
  };

  // Fixed items are applied once per measurement; shown apart from the hourly rate
  const getFixedBundlePriceSuffix = (priceBundleItems) => {
    const fixedTotal = (priceBundleItems || []).reduce((total, item) => {
      if (!item.isFixed) return total;
      const price = typeof item.price === "string" ? parseFloat(item.price.replace(",", ".")) : parseFloat(item.price || 0);
      return total + price * (item.multiplier || 1);
    }, 0);
    if (fixedTotal <= 0) return "";
    return ` + ${currency.symbol}${fixedTotal.toFixed(3)} ${t('priceBundle.fixedSuffix')}`;
  };

  const formatPriceBundle = (id) => {
    if (allPriceBundles && allPriceBundles.length > 0) {
      let priceBundle = allPriceBundles.find((priceBundle) => priceBundle.id === id);
      if (priceBundle) {
        // Determine pricing type suffix based on costBundleTypeId
        const pricingTypeSuffix = priceBundle.costBundleTypeId === costBundleTypes.TIME ? t("tableDragResources.pricePerHourSuffix") : t("tableDragResources.pricePerEventSuffix");

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {priceBundle.reference} - {priceBundle.name}
            <Chip size="extraSmall" variant={priceBundle.account.isRevenue ? "success" : "danger"}>
                {getTotalBundlePrice(priceBundle.priceBundleItems)}
              {pricingTypeSuffix}
              {getFixedBundlePriceSuffix(priceBundle.priceBundleItems)}
            </Chip>
          </div>
        );
      } else {
        return t("tableDragResources.noPriceBundle");
      }
    }
    return "";
  };

  // Helper function to get all accounts as a flat array
  const getAllAccountsForSelect = () => {
    if (!allAccounts || allAccounts.length === 0) return [];

    const companyAccounts = allAccounts.find((company) => company.id === props.companyId);
    if (!companyAccounts || !companyAccounts.accountCategories) return [];

    let allAccountsList = [];
    companyAccounts.accountCategories.forEach((category) => {
      if (category.accounts) {
        category.accounts.forEach((account) => {
          allAccountsList.push({
            id: account.id,
            name: account.name,
            category: category.name,
            displayName: `${category.name} - ${account.name}`,
          });
        });
      }
    });

    return allAccountsList;
  };

  const resetAllRowErrors = () => {
    let updatedRows = props.rows.map((row) => ({
      ...row,
      descriptionError: null,
      typeError: null,
      accountError: null,
    }));

    props.setRows(updatedRows);
  };

  const validateRow = (rowId) => {
    let errors = 0;
    let tempArray = props.rows.map((x) => ({ ...x }));
    let row = tempArray.find((x) => x.Id === rowId);

    // resetRowErrors(rowId);
    // reset errors for current row
    tempArray.find((x) => x.Id === rowId).descriptionError = null;
    tempArray.find((x) => x.Id === rowId).typeError = null;
    tempArray.find((x) => x.Id === rowId).accountError = null;

    if (row.description.trim() === "") {
      errors++;
      tempArray.find((x) => x.Id === rowId).descriptionError = t('tableDragResources.errDescriptionRequired');
    }
    if (row.type === null) {
      errors++;
      tempArray.find((x) => x.Id === rowId).typeError = t('tableDragResources.errTypeRequired');
    }

    let parsedPrice = typeof row.price === "string" ? parseFloat(row.price.replace(",", ".")) : parseFloat(row.price || 0);
    if (parsedPrice !== null && parsedPrice > 0 && row.accountId === null) {
      errors++;
      tempArray.find((x) => x.Id === rowId).accountError = t('tableDragResources.errAccountRequired');
    }

    props.setRows(tempArray);

    if (errors > 0) {
      return false;
    } else {
      return true;
    }
  };

  const validateAllRows = async () => {
    let errors = 0;

    // Reset errors for all rows before validation
    resetAllRowErrors();

    // Update rows with validation errors
    const updatedRows = props.rows.map((row) => {
      let rowErrors = 0;
      let descriptionError = null;
      let typeError = null;
      let accountError = null;

      // Validate description
      if (row.description === "") {
        descriptionError = t('tableDragResources.errDescriptionRequired');
        rowErrors++;
      }

      // Validate type
      if (row.type === null) {
        typeError = t('tableDragResources.errTypeRequired');
        rowErrors++;
      }

      // Validate price and account
      let parsedPrice = typeof row.price === "string" ? parseFloat(row.price.replace(",", ".")) : parseFloat(row.price || 0);

      if (parsedPrice !== null && parsedPrice > 0 && row.accountId === null) {
        accountError = t('tableDragResources.errAccountRequired');
        rowErrors++;
      }

      errors += rowErrors;

      // Return updated row with error messages
      return {
        ...row,
        descriptionError,
        typeError,
        accountError,
      };
    });

    // Update rows state if necessary (assuming props has a setRows method)
    props.setRows(updatedRows);

    if (errors > 0) {
      setOpenAllRowsErrorsDialog(true);
    }

    // Return validation result: true if no errors, false otherwise
    return errors === 0;
  };

  // Expose the validateAllRows function to the parent component
  useImperativeHandle(ref, () => ({
    validateAllRows,
  }));

  const handleCloseAllRowsErrorsDialog = () => {
    setOpenAllRowsErrorsDialog(false);
  };

  const rowHasErrors = (row) => {
    return row.descriptionError || row.typeError || row.accountError;
  };

  const detailHasMeasurements = async (id) => {
    const hasMeasurements = await dataService.projectDetailHasMeasurements(id);
    return hasMeasurements;
  };

  const handleCostBundleDialogClose = () => {
    setEditCostBundleDialogOpen(false);
    setPendingPriceBundleChange(null);
  };

  const handleCostBundleSave = async (rowId, newPriceBundleId, historicalChangeDate) => {
    try {
      // Call the backend API to save the cost bundle change
      const updateData = {
        projectDetailId: rowId,
        priceBundleId: newPriceBundleId,
        applyFromDate: historicalChangeDate,
        userId: props.Login.userid // Get current user ID from dataService
      };

      const response = await dataService.updateProjectDetailCostBundle(updateData);
      
      if (response.success) {
        // If successful, update the local state
        let tempArray = props.rows.map((x) => ({ ...x }));
        const rowToUpdate = tempArray.find((x) => x.Id === rowId);
        if (rowToUpdate) {
          rowToUpdate.priceBundleId = newPriceBundleId;
          
          // If historical change date is provided, store it for later use
          if (historicalChangeDate) {
            rowToUpdate.historicalPriceBundleChangeDate = historicalChangeDate;
            rowToUpdate.hasHistoricalPriceBundleChange = true;
          }
          
          // Take the row out of edit mode after successful costBundle update
          rowToUpdate.edit = false;
        }
        props.setRows(tempArray);
        
        // Show success message if available
        if (props.showSnackbar) {
          props.showSnackbar(t('tableDragResources.costBundleUpdateSuccess'), 'success');
        }
      } else {
        // Handle error response
        console.error('Failed to update cost bundle:', response.message);
        if (props.showSnackbar) {
          props.showSnackbar(t('tableDragResources.costBundleUpdateFailed'), 'error');
        }
      }
    } catch (error) {
      console.error('Error updating cost bundle:', error);
      if (props.showSnackbar) {
        props.showSnackbar(t('tableDragResources.costBundleUpdateError'), 'error');
      }
    }
    
    // Close the dialog
    handleCostBundleDialogClose();
  };

  return (
    <React.Fragment>
      {error && (
        <div style={{
          padding: '16px',
          marginBottom: '16px',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          color: '#721c24'
        }}>
          <strong>{t('tableDragResources.errorLabel')}:</strong> {error.message || t('tableDragResources.genericTableError')}
          <button 
            onClick={clearError}
            style={{
              marginLeft: '10px',
              padding: '4px 8px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            {t('tableDragResources.dismiss')}
          </button>
        </div>
      )}
      
      {isLoading && (
          <div style={{
          padding: '16px',
          textAlign: 'center',
          backgroundColor: '#d1ecf1',
          border: '1px solid #b8daff',
          borderRadius: '4px',
          color: '#0c5460',
          marginBottom: '16px'
        }}>
          {t('tableDragResources.loadingTableData')}
        </div>
      )}
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead className={classes.table__header}>
            <tr>
              <th>#</th>
              <th></th>
              <th>{t('tableDragResources.headerDescription')}</th>
              <th>{t('tableDragResources.headerType')}</th>
              <th>{t('tableDragResources.headerPriceBundle')}</th>
              <th>{t('tableDragResources.headerValueType')}</th>
              <th>{t('tableDragResources.headerDuration')}</th>
              <th className={classes.compactColumn}>{t('tableDragResources.headerKey')}</th>
              <th className={classes.compactColumn}>{t('tableDragResources.headerComment')}</th>
              <th className={classes.compactColumn}>{t('tableDragResources.headerColor')}</th>
              <th className={classes.compactColumn}></th>
            </tr>
          </thead>
          <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="droppable">
              {(provided) => (
                <tbody className={classes.table__body} {...provided.droppableProps} ref={provided.innerRef}>
                  {props.rows.map((row, index) => {
                    return (
                      <Draggable key={"Draggable_" + row.Id + "_" + index} draggableId={row.description + "_" + row.Id.toString()} className="droppable" index={index}>
                        {(provided) => (
                          <tr key={"DraggableRow_" + row.Id + "_" + index} ref={provided.innerRef} className={classes.table__row} {...provided.draggableProps}>
                            <td className={classes.indexCell}>
                              <div className={classes.listOrder}>{index + 1}</div>
                            </td>
                            <td onMouseDown={() => Expand()} onMouseUp={() => Shrink()} className={classes.dragCell} {...provided.dragHandleProps}>
                              <GripVertical />
                            </td>
                            <td>
                              {row.edit ? (
                                <Input
                                  name={`description-${row.Id}`}
                                  value={row.description || ""}
                                  onChange={(event) => setDescription(event, row.Id)}
                                  placeholder={t('tableDragResources.placeholderDescription')}
                                  error={row.descriptionError}
                                />
                              ) : (
                                <Input name={`description-${row.Id}`} value={row.description || ""} onChange={() => {}} disabled />
                              )}
                            </td>
                            <td>
                              <div className={classes.typeCell}>
                                <div className={classes.typeCell__main}>
                                  {row.edit && !row.hasMeasurements ? (
                                    <button
                                      className={`${classes.typePickerTrigger} ${row.typeError ? classes.typePickerTriggerError : ""}`}
                                      onClick={() => { setTypePickerRowId(row.Id); setTypePickerOpen(true); }}
                                    >
                                      <span className={!selectType(row) ? classes.typePickerPlaceholder : ""}>
                                        {selectType(row) || t('tableDragResources.placeholderSelectType')}
                                      </span>
                                      <ChevronDown size={14} className={classes.typePickerChevron} />
                                    </button>
                                  ) : types.length > 0 ? (
                                    row.edit && row.hasMeasurements ? (
                                      <Tooltip fullwidth content={t('tableDragResources.typeChangeBlockedTooltip')} placement="top">
                                        <Input name={`type-${row.Id}`} value={selectType(row) || ""} onChange={() => {}} disabled />
                                      </Tooltip>
                                    ) : (
                                      <Input name={`type-${row.Id}`} value={selectType(row) || ""} onChange={() => {}} disabled />
                                    )
                                  ) : null}
                                </div>
                                {getFlowBoxUsages(row).length > 0 && (
                                  <Tooltip content={renderFlowBoxTooltip(getFlowBoxUsages(row))} placement="top">
                                    <Cpu className={classes.flowBoxIcon} />
                                  </Tooltip>
                                )}
                              </div>
                            </td>
                            <td>
                              {row.edit && !isAnchorOrStickyApi(row) ? (
                                <Select value={row.priceBundleId} onChange={(value) => setPriceBundle(row.Id, value)} placeholder={t('tableDragResources.placeholderSelectPriceBundle')} fullwidth searchable>
                                  <SelectTrigger>{row.priceBundleId ? formatPriceBundle(row.priceBundleId) : ""}</SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value={null}>{t('tableDragResources.none')}</SelectItem>
                                    {getFilteredPriceBundles(row).map((priceBundle) => (
                                      <SelectItem key={priceBundle.id} value={priceBundle.id} searchText={priceBundle.name}>
                                        {formatPriceBundle(priceBundle.id)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <div className={classes.tableInputLike}>{row.priceBundleId ? formatPriceBundle(row.priceBundleId) : ""}</div>
                              )}
                            </td>
                            <td>
                              {row.edit && !isAnchorOrStickyApi(row) ? (
                                <Select value={row.valueTypeId} onChange={(value) => setValueType(row.Id, value)} placeholder={t('tableDragResources.placeholderSelectValueType')} fullwidth>
                                  <SelectTrigger>{row.valueTypeId ? formatValueType(row.valueTypeId) : ""}</SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value={null}>{t('tableDragResources.none')}</SelectItem>
                                    {valueTypes &&
                                      valueTypes.map((valueType) => (
                                        <SelectItem key={valueType.id} value={valueType.id} searchText={valueType.name}>
                                          {getValueTypeLabel(valueType)}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input name={`valueType-${row.Id}`} value={row.valueTypeId ? formatValueType(row.valueTypeId) : ""} onChange={() => {}} disabled />
                              )}
                            </td>
                            <td>
                              {row.edit && isAnchorApi(row) ? (
                                <Input
                                  name={`sessionDuration-${row.Id}`}
                                  value={row.sessionDurationMinutes ?? ""}
                                  onChange={(e) => setSessionDurationMinutes(e, row.Id)}
                                  placeholder={t('tableDragResources.placeholderMinutes')}
                                  type="number"
                                />
                              ) : (
                                <div className={classes.tableInputLike}>{isAnchorApi(row) && row.sessionDurationMinutes ? row.sessionDurationMinutes : ""}</div>
                              )}
                            </td>
                            <td className={classes.compactColumn}>
                              {row.edit && (row.type === controlTypes.FREETEXT || row.type === controlTypes.DATALIST || row.type === controlTypes.SCANNER) ? (
                                <Checkbox onClick={() => handleParent(row.Id)} checked={!!row.isParent} />
                              ) : (
                                <Checkbox disabled checked={!!row.isParent} />
                              )}
                            </td>
                            <td className={classes.compactColumn}>
                              {row.edit &&
                              (row.type === controlTypes.COUNTER || row.type === controlTypes.STOPWATCH || row.type === controlTypes.SCANNER || row.type === controlTypes.CAMERA) &&
                              !row.isParent ? (
                                <Checkbox onClick={() => handleComment(row.Id)} checked={!!row.comment} />
                              ) : (
                                <Checkbox disabled checked={!!row.comment} />
                              )}
                            </td>
                            <td className={classes.compactColumn}>
                              <ColorPicker color={props.rows.find((y) => y.Id === row.Id).color} disabled={!row.edit} id={row.Id} action={handleColor} />
                            </td>
                            <td className={isAnchorApi(row) ? classes.tripleActionColumn : classes.doubleActionColumn}>
                              <div className={classes.buttonContainer}>
                                {isAnchorApi(row) && (
                                  <Tooltip content={t('tableDragResources.qrDialogViewCodesTooltip')} placement="top">
                                    <button className={classes.qrIconButton} onClick={() => handleOpenQrDialog(row)}>
                                      <QrCode className={classes.iconButton__icon} />
                                    </button>
                                  </Tooltip>
                                )}
                                {row.edit ? (
                                  <button className={classes.iconButton} onClick={() => saveProjectRow(row.Id)}>
                                    <Save className={classes.iconButton__icon} />
                                  </button>
                                ) : (
                                  <button className={classes.iconButton} onClick={() => editProjectRow(row.Id)}>
                                    <Pencil className={classes.iconButton__icon} />
                                  </button>
                                )}
                                <button className={classes.iconButton} onClick={() => deleteProjectRow(row.Id)}>
                                  <Trash2 className={classes.iconButton__icon} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    );
                  })}
                  {expandTable ? (
                    <tr>
                      <td style={{ height: "65px" }}></td>
                    </tr>
                  ) : (
                    <React.Fragment></React.Fragment>
                  )}
                  <tr className={classes.addRow} onClick={addRow}>
                    <td className={classes.indexCell}>-</td>
                    <td className={classes.dragCell}>
                      <GripVertical />
                    </td>
                    <td>
                      <Input name="add-description" value="" onChange={() => {}} placeholder={t('tableDragResources.placeholderDescription')} disabled />
                    </td>
                    <td>
                      <Input name="add-type" value="" onChange={() => {}} placeholder={t('tableDragResources.headerType')} disabled />
                    </td>
                    <td>
                      <Input name="add-priceBundle" value="" onChange={() => {}} placeholder={t('tableDragResources.headerPriceBundle')} disabled />
                    </td>
                    <td>
                      <Input name="add-valueType" value="" onChange={() => {}} placeholder={t('tableDragResources.headerValueType')} disabled />
                    </td>
                    <td>
                      <Input name="add-sessionDuration" value="" onChange={() => {}} placeholder={t('tableDragResources.headerDuration')} disabled />
                    </td>
                    <td className={classes.compactColumn}>
                      <Checkbox disabled />
                    </td>
                    <td className={classes.compactColumn}>
                      <Checkbox disabled />
                    </td>
                    <td className={classes.compactColumn}>
                      <div className={classes.ColorPickerBorder}>
                        <div className={classes.ColorPicker}></div>
                      </div>
                    </td>
                    <td className={classes.singleActionColumn}>
                      <div className={classes.buttonContainer}>
                        <button className={classes.iconButton}>
                          <Plus className={classes.iconButton__icon} />
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              )}
            </Droppable>
          </DragDropContext>
          {/* Single row error dialog */}
          <Dialog open={open} onClose={handleDialogClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title">{t('tableDragResources.errorDialogTitle')}</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                {/* show all row errors, that are not null */}
                {props.rows.map((row) => (
                  <React.Fragment key={row.Id}>
                    {row.descriptionError && <div>{row.descriptionError}</div>}
                    {row.typeError && <div>{row.typeError}</div>}
                    {row.accountError && <div>{row.accountError}</div>}
                  </React.Fragment>
                ))}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <ButtonNormalText content={t('common.close')} onClick={handleDialogClose}>
                {t('common.close')}
              </ButtonNormalText>
            </DialogActions>
          </Dialog>

          {/* To Delete - OLD: All rows error dialog */}
          {/* <Dialog
                        open={openAllRowsErrorsDialog}
                        onClose={() => handleCloseAllRowsErrorsDialog()}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">
                          {t('tableDragResources.errorsDialogTitle')}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                {props.rows.map(row => (
                                    <div className={classes.allRowsErrors__rowContainer} key={row.Id}>
                                        {row.descriptionError && <div>{row.descriptionError}</div>}
                                        {row.typeError && <div>{row.typeError}</div>}
                                        {row.accountError && <div>{row.accountError}</div>}
                                    </div>
                                ))}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <ButtonNormalText content={t('common.close')} onClick={() => handleCloseAllRowsErrorsDialog()}>{t('common.close')}</ButtonNormalText>
                        </DialogActions>
                    </Dialog> */}

          {/* All rows error dialog */}
          <Dialog
            className={classes.dialog}
            open={openAllRowsErrorsDialog}
            aria-labelledby="draggable-dialog-title"
            onClose={() => handleCloseAllRowsErrorsDialog()}
            classes={{ paper: classes.dialogPaper }}
          >
            <div className={classes.dialog__head}>
              <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
                {t('tableDragResources.errorsDialogTitle')}
              </DialogTitle>
              <button onClick={() => handleCloseAllRowsErrorsDialog()} className={classes.dialog__closeButton}>
                <X size={19} />
              </button>
            </div>
            <DialogContent className={classes.dialog__contentContainer}>
              <div className={classes.dialog__content}>
                {/* show all (unique) row errors, that are not null */}
                {props.rows.map((row, index) => {
                  return rowHasErrors(row) ? ( // Explicitly return the JSX
                    <div className={classes.allRowsErrors__rowContainer} key={row.Id}>
                      <p className={classes.rowContainer__title}>{t('tableDragResources.rowLabel', { index: index + 1 })}</p>
                      <div className={classes.rowContainer__errorList}>
                        {row.descriptionError && (
                          <div className={classes.errorListItem}>
                            <div className={classes.errorListItem__bullet} />
                            {row.descriptionError}
                          </div>
                        )}
                        {row.typeError && (
                          <div className={classes.errorListItem}>
                            <div className={classes.errorListItem__bullet} />
                            {row.typeError}
                          </div>
                        )}
                        {row.accountError && (
                          <div className={classes.errorListItem}>
                            <div className={classes.errorListItem__bullet} />
                            {row.accountError}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null; // Return null if rowHasErrors is false
                })}
              </div>
            </DialogContent>
          </Dialog>
        </table>
      </div>

      {/* Anchor API QR Dialog */}
      <QrCodeDialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        title={qrDialogApiName}
        qrItems={qrDialogLoading ? undefined : qrDialogItems}
        showCopyButton={!qrDialogLoading && qrDialogItems.length > 0}
        showPrintButton={!qrDialogLoading && qrDialogItems.length > 0}
      >
        {qrDialogLoading ? (
          <div style={{ width: "100%", padding: "32px", textAlign: "center", color: "#666", fontSize: "14px" }}>{t('tableDragResources.loadingEnumOptions')}</div>
        ) : qrDialogItems.length === 0 ? (
          <div style={{ width: "100%", padding: "32px", textAlign: "center", color: "#666", fontSize: "14px" }}>{t('tableDragResources.noEnumOptionsDefined')}</div>
        ) : null}
      </QrCodeDialog>

      {/* Type Picker Dialog */}
      <TypePickerDialog
        open={typePickerOpen}
        onClose={() => setTypePickerOpen(false)}
        onSelect={(value) => handleTypeSelectChange(value, typePickerRowId)}
        companyId={props.companyId}
        lists={lists}
        apis={apis}
        hasAnchorApi={typePickerRowId ? hasAnchorApiInProject(typePickerRowId) : false}
      />

      {/* Edit Cost Bundle Dialog */}
      {pendingPriceBundleChange && (
        <EditCostBundleDialog
          dialogOpen={editCostBundleDialogOpen}
          closeDialog={handleCostBundleDialogClose}
          rowId={pendingPriceBundleChange.rowId}
          newPriceBundleId={pendingPriceBundleChange.newPriceBundleId}
          oldPriceBundleId={pendingPriceBundleChange.oldPriceBundleId}
          rowDescription={pendingPriceBundleChange.rowDescription}
          allPriceBundles={allPriceBundles}
          currency={currency}
          onSave={handleCostBundleSave}
        />
      )}
    </React.Fragment>
  );
});

export default TableDragResources;

const useStyles = makeStyles({
  // Table styles matching PriceBundleTable
  tableContainer: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    borderSpacing: "0",

    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
  },

  table__header: {
    backgroundColor: "#DFDFDF",
    borderRadius: "8px 8px 0px 0px",

    "& th": {
      height: "36px",
      padding: "0px 16px",
      textAlign: "left",
      fontSize: "16px",
      fontWeight: "normal",
      color: "#0C0E29",

      "&:first-child": {
        borderTopLeftRadius: "8px",
        width: "50px",
        textAlign: "center",
      },

      "&:nth-child(2)": {
        width: "40px",
      },

      "&:last-child": {
        borderTopRightRadius: "8px",
        width: "fit-content",
        textAlign: "center",
      },
    },
  },

  table__body: {
    "& tr": {
      borderBottom: "1px solid #EDEDED",

      "&:last-child": {
        borderBottom: "none",

        "& td": {
          "&:first-child": {
            borderBottomLeftRadius: "8px",
          },

          "&:last-child": {
            borderBottomRightRadius: "8px",
          },
        },
      },

      "& td": {
        height: "48px",
        padding: "0px 16px",
        textAlign: "left",
        fontSize: "16px",
        fontWeight: "normal",
        color: "#0C0E29",

        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        // maxWidth: 200,

        borderRight: "1px solid #EDEDED",
      },
    },
  },

  table__row: {
    backgroundColor: "#FFFFFF !important",
  },

  // Drag and drop specific styles
  indexCell: {
    display: "table-cell",
    borderRight: "2px solid #EDEDED !important",
    width: "50px !important",
    background: "#DFDFDF",
    color: "#0C0E29",
    textAlign: "center !important",
    fontWeight: "500",
    zIndex: "2",
  },

  dragCell: {
    border: "none !important",
    width: "40px !important",
    padding: "8px !important",
    cursor: "grab",
    backgroundColor: "#F8F8F8",

    "&:hover": {
      backgroundColor: "#EDEDED",
    },

    "&:active": {
      cursor: "grabbing",
    },
  },

  listOrder: {
    paddingLeft: "5px",
    paddingRight: "5px",
  },

  // Action buttons
  buttonContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },

  iconButton: {
    height: "32px",
    width: "32px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: "4px",
    border: "none",

    backgroundColor: "#F8F8F8",
    cursor: "pointer",

    "&:hover": {
      backgroundColor: "#EDEDED",
    },
  },

  iconButton__icon: {
    height: "20px",
    width: "20px",

    display: "block",
  },

  apiItemContent: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },

  apiItemIcon: {
    width: "18px",
    height: "18px",
    strokeWidth: "1.4px",
    flexShrink: 0,
    color: "#0C0E29",
  },

  // Add row styling
  addRow: {
    opacity: "0.5",

    "& td": {
      backgroundColor: "#F8F8F8 !important",
    },

    "&:hover": {
      opacity: "0.8",
      cursor: "pointer",

      "& td": {
        backgroundColor: "#EDEDED !important",
      },
    },
  },

  // Form controls
  descriptionField: {
    width: "100%",

    "& input": {
      fontSize: "14px",
      padding: "8px 12px",
    },
  },

  measurementPrice: {
    width: "100%",

    "& input": {
      fontSize: "14px",
      padding: "8px 12px",
    },
  },

  // Custom select styling for table context
  tableSelect: {
    width: "100%",
    minWidth: "150px",

    "& .Select": {
      fontSize: "14px",
    },
  },

  // Custom input styling for table context
  tableInput: {
    width: "100%",

    "& input": {
      fontSize: "14px",
      padding: "8px 12px",
      border: "1px solid #DCDCDC",
      borderRadius: "4px",

      "&:focus": {
        borderColor: "var(--color-primary)",
        outline: "none",
      },

      "&:disabled": {
        backgroundColor: "#F8F8F8",
        color: "#666",
      },
    },
  },

  // Type picker trigger button (replaces the Select dropdown)
  typePickerTrigger: {
    width: "100%",
    minWidth: "150px",
    height: "40px",
    fontSize: "14px",
    padding: "0 10px 0 12px",
    border: "1px solid #DCDCDC",
    borderRadius: "4px",
    backgroundColor: "#FFFFFF",
    color: "#0C0E29",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "6px",
    cursor: "pointer",
    boxSizing: "border-box",
    textAlign: "left",
    "&:hover": {
      borderColor: "#0C0E29",
    },
  },

  typePickerTriggerError: {
    borderColor: "#D84949 !important",
  },

  typePickerPlaceholder: {
    color: "#9ca3af",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  typePickerChevron: {
    color: "#9ca3af",
    flexShrink: 0,
  },

  // Type cell wrapper so the FlowBox indicator sits to the right of the type input
  typeCell: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  typeCell__main: {
    flex: 1,
    minWidth: 0,
  },

  flowBoxIcon: {
    width: "18px",
    height: "18px",
    flexShrink: 0,
    color: "var(--color-primary)",
    display: "block",
  },

  flowBoxTooltip: {
    textAlign: "left",
    fontSize: "13px",
    color: "#0C0E29",
    padding: "2px",
    minWidth: "180px",
  },

  flowBoxTooltip__title: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "#9ca3af",
    marginBottom: "8px",
  },

  flowBoxTooltip__name: {
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: 1.3,
  },

  flowBoxTooltip__imei: {
    fontFamily: "monospace",
    fontSize: "12px",
    color: "#6b7280",
    marginBottom: "6px",
  },

  flowBoxTooltip__buttons: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },

  flowBoxTooltip__buttonRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    color: "#0C0E29",
  },

  flowBoxTooltip__dome: {
    width: "14px",
    height: "14px",
    borderRadius: "50%",
    flexShrink: 0,
    boxShadow: "inset 0 1px 1px rgba(255,255,255,0.35), inset 0 -1px 2px rgba(0,0,0,0.3), 0 0 0 1px rgba(0,0,0,0.12)",
  },

  flowBoxTooltip__detail: {
    fontSize: "13px",
    color: "#374151",
  },

  // Custom div styling to match disabled input appearance
  tableInputLike: {
    width: "100%",
    height: "40px",
    fontSize: "16px",
    padding: "8px 12px",
    border: "1px solid #DCDCDC",
    borderRadius: "4px",
    backgroundColor: "#F8F8F8",
    color: "#0C0E29",
    minHeight: "20px",
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
  },

  // Color picker
  ColorPickerBorder: {
    padding: "5px",
    width: "46px",
    height: "24px",
    borderRadius: "2px",
    boxShadow: "0 0 0 1px rgba(0,0,0,.1)",
    background: "white",
    marginBottom: "5px",
  },

  ColorPicker: {
    width: "36px",
    height: "14px",
    borderRadius: "2px",
  },

  // Compact column styles
  compactColumn: {
    width: "fit-content !important",
    textAlign: "center !important",
    padding: "0px 8px !important",
    whiteSpace: "nowrap !important",
  },

  doubleActionColumn: {
    width: "72px !important",
    textAlign: "center !important",
    padding: "0px 8px !important",
    whiteSpace: "nowrap !important",
    borderRight: "none !important",
  },
  tripleActionColumn: {
    width: "112px !important",
    textAlign: "center !important",
    padding: "0px 8px !important",
    whiteSpace: "nowrap !important",
    borderRight: "none !important",
  },
  qrIconButton: {
    height: "32px",
    width: "32px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: "4px",
    border: "none",

    backgroundColor: "#F0E8DF",
    cursor: "pointer",
    color: "var(--color-primary)",

    "&:hover": {
      backgroundColor: "#E4D5C3",
    },
  },
  singleActionColumn: {
    width: "40px !important",
    textAlign: "center !important",
    padding: "0px 8px !important",
    whiteSpace: "nowrap !important",
    borderRight: "none !important",
  },

  // Dialog styles
  dialog: {
    margin: "auto",
  },

  dialogPaper: {
    width: "400px !important",
    height: "auto",
    borderRadius: "6px !important",
  },

  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",

    padding: "16px 16px 0px 16px !important",
    marginBottom: "16px !important",
  },

  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",
    lineHeight: "29px !important",

    padding: "0px !important",
  },

  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    height: "25px !important",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "var(--color-primary) auto 1px",
    },
  },

  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "16px",

    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },

  dialog__content: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",

    margin: "0px !important",
  },

  allRowsErrors__rowContainer: {
    width: "100%",
    boxSizing: "border-box",

    display: "flex",
    flexDirection: "column",

    backgroundColor: "#F8F8F8",

    borderRadius: "4px",

    padding: "8px",
    marginBottom: "8px",
  },

  rowContainer__title: {
    fontSize: "16px",

    margin: "0px",
    marginBottom: "8px",
  },

  rowContainer__errorList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",

    opacity: ".8",

    margin: "0px",
  },

  errorListItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",

    marginLeft: "4px",
  },

  errorListItem__bullet: {
    width: "4px",
    height: "4px",
    borderRadius: "50%",
    backgroundColor: "#0C0E29",
  },

  // Legacy styles to maintain compatibility
  tableContent: {
    paddingTop: "15px",
  },
  tableHead: {
    marginBottom: "15px",
  },
  tableTd: {
    padding: "5px",
  },
  tableRow: {
    tableLayout: "fixed",
  },
  indexTd: {
    display: "table-cell",
    borderRight: "2px solid rgb(224, 224, 224)",
    width: "25px",
    background: "rgba(0, 0, 0, 0.54)",
    color: "white",
    zIndex: "2",
  },
  emptydrag: {
    padding: "17px !important",
    border: "0",
  },
  dragTd: {
    border: "0",
    width: "24px",
  },
  dragIcon: {
    float: "left",
  },
  editButton: {
    fontSize: "2rem !important",
    color: "#0B4097 !important",
  },
  functionButton: {
    fontSize: "2rem !important",
  },
});
